import { useState, useRef, useEffect } from 'react';
import { ChevronDown } from 'lucide-react';

interface Option {
  name: string;
  value: string;
  order: string; // Added the missing 'order' property
}

interface SortingDropdownProps {
  options: Option[];
  setSortValue?: (value: { value: string; order: string }) => void;
  setFilterMode?: (value: string) => void;
  setParameters: (params: {
    id: number;
    pageNumber: number;
    stockStatus: string;
    value: string;
    order: string;
  }) => void;
  setPageNumber: (pageNumber: number) => void;
  brandName: string[];
  stockStatus: string;
  categoryId?: number;
}

const SortingDropdown: React.FC<SortingDropdownProps> = ({ options, setParameters, setPageNumber, stockStatus }) => {
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [selectedOption, setSelectedOption] = useState<string>('Default sorting');
  const dropdownRef = useRef<HTMLDivElement | null>(null);

  const toggleDropdown = (): void => {
    setIsOpen((prev) => !prev);
  };

  const handleOptionClick = (option: Option): void => {
    setSelectedOption(option.name);
    setIsOpen(false);
    setParameters({
          id: 0, // Using category.id directly
          pageNumber: 1,
          stockStatus: stockStatus,
          value: option.value,
          order: option.order,
        });
    setPageNumber(1);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent): void => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  return (
    <div ref={dropdownRef} className="relative w-fit mt-mob-20 sm:mt-0 lg:mt-web-12 lg:ml-auto">
      {/* Dropdown Toggle */}
      <div 
        className="w-fit h-mob-40 sm:h-tab-42 lg:h-web-46 px-mob-15 sm:px-tab-15 lg:px-web-15 py-mob-5 sm:py-tab-5 lg:py-web-5 gap-mob-15 sm:gap-tab-20 lg:gap-web-20 flex items-center justify-between border border-gray-200 rounded-mob-6 sm:rounded-tab-6 lg:rounded-web-6 bg-white cursor-pointer"
        onClick={toggleDropdown}
      >
        <p className="text-[#888] font-medium text-mob-16 sm:text-tab-16 lg:text-web-20 leading-[120%]">{selectedOption}</p>
        <ChevronDown
          size={18}
          className={`mt-mob-2 sm:mt-tab-3 lg:mt-web-4 transform transition-transform duration-300 ease-in-out text-[#888] ${isOpen ? 'rotate-180' : 'rotate-0'}`}
        />
      </div>

      {/* Dropdown List with Smooth Animation */}
      <div
        className={`absolute top-full left-0 lg:left-auto lg:right-0 mt-1 w-64 bg-white border border-gray-200 rounded-mob-10 sm:rounded-tab-10 lg:rounded-web-10 overflow-hidden shadow-lg z-10 transition-all duration-300 ease-in-out transform origin-top ${
          isOpen ? 'opacity-100 scale-y-100 pointer-events-auto' : 'opacity-0 scale-y-0 pointer-events-none'
        }`}
      >
        {options.map((option, index) => (
          <div
            key={index}
            className="px-mob-15 sm:px-tab-15 lg:px-web-15 py-mob-8 sm:py-tab-8 lg:py-web-8 text-mob-16 sm:text-tab-16 lg:text-web-20 leading-[120%] hover:bg-PrimaryLightBlue hover:text-white cursor-pointer transition-colors text-gray-700"
            onClick={() => handleOptionClick(option)}
          >
            {option.name}
          </div>
        ))}
      </div>
    </div>
  );
};

export default SortingDropdown;
